import { Button, ButtonProps } from "@mui/material";
import { useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router";
import { Book, BookExerciseKind, BookKind, BookLimit } from "../../../model/book/Book";
import { RoleKind } from "../../../model/Role";
import { actionSetPickDialog, actionSetVIPDialog } from "../../../store/dialog/reducer";
import {
    actionCreateExamByBook,
    actionOpenExam,
    actionOpenReportByBook,
    actionRestartExamByBook,
} from "../../../store/exam/action";
import { actionSetCreateDialog } from "../../../store/exam/reducer";
import { useAppDispatch } from "../../../store/hooks";
import { selectUser } from "../../../store/user/selector";

const GridButton = (props: ButtonProps) => {
    return (
        <Button size="small" {...props} sx={{ m: "10px", ...props.sx }}>
            {props.children}
        </Button>
    );
};

export function LoginButton() {
    const navigate = useNavigate();
    return <GridButton onClick={() => navigate("/login")}>登录使用</GridButton>;
}

export function OpenReportButton(props: { book: Book }) {
    const { book } = props;
    const dispatch = useAppDispatch();
    const navigate = useNavigate();
    return (
        <GridButton
            onClick={() => dispatch(actionOpenReportByBook(book, navigate))}
            disabled={isDisabled(book)}
            color="bbcontinue"
        >
            查看报告
        </GridButton>
    );
}

export function StartButton(props: { book: Book }) {
    const { book } = props;
    return (
        <>
            {book.limit === BookLimit.Forbid ? (
                <NoPermissionStartButton book={book} />
            ) : (
                <HasPermissionStartButton book={book} />
            )}
        </>
    );
}

export function HasPermissionStartButton(props: { book: Book }) {
    const { book } = props;
    const dispatch = useAppDispatch();
    const navigate = useNavigate();
    const location = useLocation();

    const title = book.template.kind === BookKind.Simulate ? "创建模考" : "开始练习";
    return (
        <GridButton
            onClick={() => {
                if (
                    book.template.kind === BookKind.Exercise &&
                    (book.template.kind4 === BookExerciseKind.Pick ||
                        book.template.kind4 === BookExerciseKind.Best)
                ) {
                    dispatch(actionSetPickDialog({ show: true, tempID: book.template.id }));
                } else if (book.template.kind === BookKind.Simulate) {
                    dispatch(actionSetCreateDialog(true));
                } else {
                    dispatch(
                        actionCreateExamByBook(
                            book.id,
                            navigate,
                            location.pathname + location.search
                        )
                    );
                }
            }}
            disabled={isDisabled(book)}
        >
            {title}
        </GridButton>
    );
}

export function NoPermissionStartButton(props: { book: Book }) {
    const dispatch = useAppDispatch();
    return (
        <GridButton color="bblabelred" onClick={() => dispatch(actionSetVIPDialog(true))}>
            开始练习
        </GridButton>
    );
}

export function ContinueButton(props: { book: Book }) {
    const { book } = props;
    const dispatch = useAppDispatch();
    const navigate = useNavigate();
    const location = useLocation();
    return (
        <GridButton
            color="bbcontinue"
            onClick={() =>
                dispatch(
                    actionOpenExam(book.exam_id, navigate, location.pathname + location.search)
                )
            }
            disabled={isDisabled(book)}
        >
            继续考试
        </GridButton>
    );
}

export function ReviewButton(props: { book: Book }) {
    const { book } = props;
    const dispatch = useAppDispatch();
    const navigate = useNavigate();
    let disabled = isDisabled(book);

    const user = useSelector(selectUser);
    // 普通会员可以浏览 非付费的练习册
    // TODO: 梳理这里的逻辑
    if (
        user.authority.role_kind !== RoleKind.Stranger &&
        !(
            book.template.kind === BookKind.Tailor ||
            book.template.kind === BookKind.High ||
            book.template.kind === BookKind.Higher
        )
    ) {
        disabled = false;
    }
    const location = useLocation();
    return (
        <GridButton
            sx={{ borderWidth: "3px", bgcolor: isActive(book) ? "white" : undefined }}
            variant="outlined"
            color="bbreview"
            onClick={() =>
                dispatch(
                    actionOpenExam(book.exam_id, navigate, location.pathname + location.search)
                )
            }
            disabled={disabled}
        >
            浏览试卷
        </GridButton>
    );
}

export function RestartButton(props: { book: Book }) {
    const { book } = props;
    const dispatch = useAppDispatch();
    const navigate = useNavigate();
    const location = useLocation();
    return (
        <GridButton
            sx={{ color: "white" }}
            color="bbrestart"
            disabled={isDisabled(book)}
            onClick={() => {
                if (
                    book.template.kind === BookKind.Exercise &&
                    (book.template.kind4 === BookExerciseKind.Pick ||
                        book.template.kind4 === BookExerciseKind.Best)
                ) {
                    dispatch(actionSetPickDialog({ show: true, tempID: book.template.id }));
                } else {
                    dispatch(
                        actionRestartExamByBook(
                            book.id,
                            navigate,
                            location.pathname + location.search
                        )
                    );
                }
            }}
        >
            重新考试
        </GridButton>
    );
}

function isActive(b: Book): boolean {
    return b.limit === BookLimit.Free;
}

function isDisabled(b: Book): boolean {
    return !isActive(b);
}
